import React, { useState, useEffect } from 'react';
import { Card } from "./ui/Card";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription
} from "./ui/Dialog";
import { Button } from "./ui/Button";
import { Users, UserCheck, Lock, ExternalLink } from "lucide-react";
import { useUsersApi } from '../apis/users-apis';
import { AccessType, UserData } from '../types';

interface AccessDisplayProps {
  viewAccessType: AccessType;
  editAccessType: AccessType;
  viewAllowedUsers: string[];
  editAllowedUsers: string[];
  ownerEmail: string;
  ownerName?: string;
}

const AllowedUsersList = ({ userIds }: { userIds: string[] }) => {
  const [users, setUsers] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const { getUsers } = useUsersApi();
  
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersData = await getUsers();
        const userMap = new Map();
        usersData.forEach((user: UserData) => {
          userMap.set(user.userId, user.email);
        });
        setUsers(userMap);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  if (isLoading) {
    return <div className="px-6 py-4 text-gray-600">Loading users...</div>;
  }

  return (
    <div className="px-6 py-4">
      <div className="divide-y divide-gray-200 max-h-[60vh] overflow-y-auto">
        {userIds.map((userId) => (
          <div key={userId} className="flex items-center gap-2 py-3">
            <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
            <div className="text-sm text-gray-600">
              {users.get(userId) || 'Unknown user'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AccessDisplay: React.FC<AccessDisplayProps> = ({ 
  viewAccessType,
  editAccessType,
  viewAllowedUsers,
  editAllowedUsers,
  ownerEmail,
  ownerName,
}) => {
  const getIcon = (accessType: AccessType) => {
    switch (accessType) {
      case 'All Users':
        return <Users className="h-5 w-5 text-blue-500" />;
      case 'Allowed Users':
        return <UserCheck className="h-5 w-5 text-green-500" />;
      case 'Owner Only':
        return <Lock className="h-5 w-5 text-orange-500" />;
    }
  };

  const renderAccessSection = (accessType: AccessType, allowedUsers: string[], label: string) => {
    const getAccessDescription = () => {
      switch (accessType) {
        case 'All Users':
          return <div className="text-gray-600">Everyone has {label.toLowerCase()} access</div>;
        case 'Allowed Users':
          return (
            <div className="text-gray-600">
              Access limited to specific users {' '}
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="link"
                    className="text-blue-500 p-0 h-auto font-normal hover:no-underline"
                  >
                    <ExternalLink className="h-4 w-4 inline-block mr-1" />
                    View users
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{label} - Allowed Users</DialogTitle>
                    <DialogDescription>
                      Users who have been granted {label.toLowerCase()} permissions
                    </DialogDescription>
                  </DialogHeader>
                  <AllowedUsersList userIds={allowedUsers} />
                </DialogContent>
              </Dialog>
            </div>
          );
        case 'Owner Only':
          return <div className="text-gray-600">Only the owner has {label.toLowerCase()} access</div>;
      }
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          {getIcon(accessType)}
          <h3 className="font-medium text-gray-900">{label}</h3>
        </div>
        <div className="pl-7">
          {getAccessDescription()}
        </div>
      </div>
    );
  };

  return (
    <Card className="p-6 space-y-6">
      {/* Owner information shown once at the top */}
      <div className="flex items-center gap-2 pb-4 border-b">
        <div className="w-2 h-2 bg-blue-500 rounded-full"/>
        <div className="text-sm text-gray-600">
          Owner: {ownerName || ownerEmail}
        </div>
      </div>

      {/* Access sections */}
      <div className="space-y-6">
        {renderAccessSection(viewAccessType, viewAllowedUsers, "View Access")}
        {renderAccessSection(editAccessType, editAllowedUsers, "Edit Access")}
      </div>
    </Card>
  );
};

export default AccessDisplay;